/**
 * useVehicleInsuranceData - Hook for fetching vehicle insurance data
 *
 * Provides:
 * - "État de Parc" formatted data (matching export format)
 * - Insurance contracts list
 * - Claims for this vehicle
 *
 * @param {number|string} vehicleId - The vehicle ID
 * @returns {Object} Insurance data and handlers
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api.js';
import { insuranceModuleAPI } from '../api/insurance.js';
import { isFlagOn } from '../utils/dbFlags.js';

// État de Parc field configuration (matching export format)
export const ETAT_DE_PARC_FIELDS = [
  { key: 'vehicule', label: 'VÉHICULE', format: 'text' },
  { key: 'assureur', label: 'ASSUREUR', format: 'text' },
  { key: 'coutAssurance', label: 'COÛT ASSURANCE', format: 'currency' },
  { key: 'immatriculation', label: 'IMMATRICULATION', format: 'text' },
  { key: 'premiereMiseEnCirculation', label: '1ère MISE EN CIRCULATION', format: 'date' },
  { key: 'kilometrage', label: 'KILOMÉTRAGE', format: 'km' },
  { key: 'garantie', label: 'GARANTIE', format: 'text' },
  { key: 'volIncendie', label: 'VOL / INCENDIE', format: 'boolean' },
  { key: 'brisDeGlace', label: 'BRIS DE GLACE', format: 'boolean' },
  { key: 'assistance0km', label: 'ASSISTANCE 0 KM', format: 'boolean' },
  { key: 'valeurMarchande', label: 'VALEUR MARCHANDE', format: 'currency' }
];

// Coverage level labels
const COVERAGE_LABELS = {
  'TIERS': 'Tiers',
  'TIERS_PLUS': 'Tiers Plus',
  'TOUS_RISQUES': 'Tous Risques',
  'ECONOMIQUE': 'Économique'
};

// Claim type labels
export const CLAIM_TYPE_LABELS = {
  theft: 'Vol',
  collision: 'Collision',
  breakdown: 'Panne',
  hail: 'Grêle',
  fire: 'Incendie',
  bodily_injury: 'Dommages corporels',
  glass_breakage: 'Bris de glace',
  other: 'Autre'
};

// Claim statuses considered closed/terminal (no longer "in progress").
// Used by UI counters ("N en cours") across VehicleInsuranceTab,
// ContractDetailModal, ClaimsView, reports, etc. Keep in sync with backend.
export const CLOSED_CLAIM_STATUSES = [
  'cloture',
  'abandonne',
  'vei_conserve',
  'vei_cede',
  // Legacy EN
  'closed',
  'settled',
  'denied',
  'rejected',
  'indemnified',
];

// Claim status labels — keys must match backend claim.status values
// (both modern FR slugs and legacy EN for backward compat)
export const CLAIM_STATUS_LABELS = {
  // Modern FR statuses
  brouillon: 'Brouillon',
  declaration_prete: 'Déclaration prête',
  declaration_envoyee: 'Déclaration envoyée',
  en_instruction: 'En instruction',
  expert_pending: 'Expert désigné',
  expertise_realisee: 'Expertise réalisée',
  rapport_recu: 'Rapport expert reçu',
  prise_en_charge_demandee: 'PEC demandée',
  en_reparation: 'En réparation',
  reparation_terminee: 'Réparation terminée',
  attente_facture_garage: 'Attente facture garage',
  attente_indemnisation: 'Attente indemnisation',
  cloture: 'Clôturé',
  abandonne: 'Abandonné',
  vei_conserve: 'VEI — Véhicule conservé',
  vei_cede: 'VEI — Véhicule cédé',
  // Legacy EN statuses (backward compat)
  draft: 'Brouillon',
  declared: 'Déclaré',
  acknowledged: 'Pris en compte',
  expert_done: 'Rapport expert',
  repair_authorized: 'Réparation autorisée',
  indemnified: 'Indemnisé',
  closed: 'Clôturé',
  rejected: 'Rejeté'
};

export function useVehicleInsuranceData(vehicleId, vehicle = null) {
  const [vehicleInsurance, setVehicleInsurance] = useState([]);
  const [insuranceContracts, setInsuranceContracts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [coverageBlocks, setCoverageBlocks] = useState([]);
  const [coverageModels, setCoverageModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all insurance data for the vehicle
  const fetchData = useCallback(async () => {
    if (!vehicleId) return;

    setLoading(true);
    setError(null);

    try {
      const vid = parseInt(vehicleId);
      const [viRes, contractsRes, claimsRes, modelsRes] = await Promise.all([
        api.vehicleInsurance?.getByVehicle?.(vid).catch(() => ({ data: [] })) || Promise.resolve({ data: [] }),
        api.insurance?.getAll?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] }),
        api.claim?.getAll?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] }),
        // coverage_model holds the canonical name for each covertage (Tous
        // Risques / Tiers + / …). Without this fetch, vi.coverage_model_id
        // never resolves to a name and the modal shows "N/A" everywhere.
        api.coverageModel?.getAll?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] })
      ]);

      const viRecords = (viRes.data || []).filter(vi => !vi.removed_at);
      setVehicleInsurance(viRecords);
      setInsuranceContracts(contractsRes.data || []);
      setClaims((claimsRes.data || []).filter(c => parseInt(c.vehicle_id) === vid));
      setCoverageModels(modelsRes.data || []);

      // Fetch coverage blocks for linked contracts
      const contractIds = [...new Set(viRecords.map(vi => vi.contract_id).filter(Boolean))];
      const allBlocks = [];
      for (const cid of contractIds) {
        try {
          const res = await insuranceModuleAPI.getContractCoverage(cid);
          const blocks = res.data || res || [];
          allBlocks.push(...blocks);
        } catch { /* ignore */ }
      }
      setCoverageBlocks(allBlocks);
    } catch (err) {
      console.error('Error fetching vehicle insurance data:', err);
      setError(err.message || 'Failed to load insurance data');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  // Auto-fetch on mount and when vehicle changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get primary insurance (is_primary = 1 or first one)
  const primaryInsurance = useMemo(() => {
    const primary = vehicleInsurance.find(vi => isFlagOn(vi.is_primary));
    return primary || vehicleInsurance[0] || null;
  }, [vehicleInsurance]);

  // Get contract for primary insurance
  const primaryContract = useMemo(() => {
    if (!primaryInsurance) return null;
    return insuranceContracts.find(c => c.id === primaryInsurance.contract_id) || null;
  }, [primaryInsurance, insuranceContracts]);

  // Build "État de Parc" data object
  const etatDeParc = useMemo(() => {
    if (!vehicle) return null;

    const vi = primaryInsurance;
    const contract = primaryContract;

    // Check coverage blocks for guarantee presence (new system)
    const blockCodes = coverageBlocks.map(b => (b.code || '').toUpperCase());
    const hasBlock = (codes) => codes.some(c => blockCodes.includes(c));

    // Derive booleans: prefer coverage_blocks, fallback to legacy contract fields
    const hasTheftFire = hasBlock(['THEFT', 'FIRE', 'VOL', 'INCENDIE'])
      || contract?.includes_theft_fire === 1;
    const hasGlass = hasBlock(['GLASS', 'BRIS_GLACE', 'GLASS_BREAKAGE'])
      || contract?.includes_glass_breakage === 1;
    const hasAssistance = hasBlock(['ASSISTANCE_0KM', 'ASSISTANCE'])
      || contract?.includes_assistance_0km === 1;

    // Coverage model name — résolu depuis coverage_model_id (source unique).
    // La colonne string legacy n'est plus lue (cleanup dual-schema).
    const resolvedModel = vi?.coverage_model_id
      ? coverageModels.find(m => m.id === vi.coverage_model_id)
      : null;
    const coverageModelName = resolvedModel?.name
      || COVERAGE_LABELS[vi?.coverage_level]
      || vi?.coverage_level
      || 'N/A';

    return {
      vehicule: `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'N/A',
      assureur: contract?.insurer_name || 'Non assuré',
      coutAssurance: vi?.premium_amount != null ? vi.premium_amount : (contract?.total_premium_ttc ?? 0),
      immatriculation: vehicle.license_plate || 'N/A',
      premiereMiseEnCirculation: vehicle.first_registration_date || null,
      kilometrage: vehicle.mileage || 0,
      garantie: coverageModelName,
      volIncendie: hasTheftFire,
      brisDeGlace: hasGlass,
      assistance0km: hasAssistance,
      valeurMarchande: vehicle.market_value || vehicle.purchase_price || null
    };
  }, [vehicle, primaryInsurance, primaryContract, coverageBlocks, coverageModels]);

  // Contracts with enriched data
  const enrichedContracts = useMemo(() => {
    return vehicleInsurance.map(vi => {
      const contract = insuranceContracts.find(c => c.id === vi.contract_id);
      // Resolve coverage_model_id → { id, name, level } so the display
      // code can show the canonical model name (Tous Risques / Tiers + / …)
      // instead of the raw id or "N/A".
      const coverageModel = vi.coverage_model_id
        ? coverageModels.find(m => m.id === vi.coverage_model_id) || null
        : null;
      return {
        ...vi,
        contract,
        coverage_model: coverageModel,
        isPrimary: isFlagOn(vi.is_primary),
        // Calculate days until expiry
        daysUntilExpiry: contract?.expiry_date
          ? Math.ceil((new Date(contract.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
          : null
      };
    });
  }, [vehicleInsurance, insuranceContracts, coverageModels]);

  // Claims statistics
  const claimsStats = useMemo(() => {
    const openClaims = claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status));
    const closedClaims = claims.filter(c => CLOSED_CLAIM_STATUSES.includes(c.status));
    const totalEstimated = claims.reduce((sum, c) => sum + (parseFloat(c.estimated_amount) || 0), 0);
    const totalIndemnified = claims.reduce((sum, c) => sum + (parseFloat(c.indemnity_amount) || 0), 0);

    return {
      total: claims.length,
      open: openClaims.length,
      closed: closedClaims.length,
      totalEstimated,
      totalIndemnified
    };
  }, [claims]);

  return {
    // Raw data
    vehicleInsurance,
    insuranceContracts,
    claims,
    coverageBlocks,
    // Computed
    primaryInsurance,
    primaryContract,
    etatDeParc,
    enrichedContracts,
    claimsStats,
    // State
    loading,
    error,
    reload: fetchData
  };
}

export default useVehicleInsuranceData;
